import type { OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { UserManagementService } from '../../core/services/user-management.service';
import type { IUser, SystemRole } from '../../shared/models/user.model';
import { Subject, takeUntil } from 'rxjs';
import config from '../../../config';

@Component({
  selector: 'app-market-admin-dashboard',
  templateUrl: './market-admin-dashboard.component.html',
  styleUrls: ['./market-admin-dashboard.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, RouterModule]
})
export class MarketAdminDashboardComponent implements OnInit, OnDestroy, OnChanges {
  private roleEffectCleanup: (() => void) | null = null;
  private destroy$ = new Subject<void>();
  private authService = inject(AuthService);
  private userService = inject(UserManagementService);
  private cdr = inject(ChangeDetectorRef);

  // Inputs from parent dashboard component
  userRoleInput = input<string>('');
  currentUserInput = input<IUser | null>(null);

  errorMessages: string | undefined;
  readonly loadingEvent = output<boolean>();

  // Local user information
  userRole = '';
  userRoleCode = '';
  userName = '';
  userMarket = '';
  userMarketId: number | null = null;
  userBusinessUnits = '';

  // Privileges from backend
  userPrivileges = new Set<string>();

  // Reports dashboard URL
  reportsDashboardUrl = config.environment_name === 'dev'
    ? 'https://app.powerbi.com/groups/3b6b1601-c670-4e6e-b863-f1835a30051a/reports/ba26883d-18c6-40d8-b881-4512b3328f99/b7690b443e0a86b4b19a?experience=power-bi'
    : 'https://app.powerbi.com/Redirect?action=OpenApp&appId=5619a16c-49cd-440c-9cdb-72be2d9f7941&ctid=af8e89a3-d9ac-422f-ad06-cc4eb4214314&experience=power-bi';

  ngOnInit(): void {
    this.loadingEvent.emit(true);

    // Subscribe to selectedRoleContext$ to get the EXACT role with market_id
    this.authService.selectedRoleContext$
      .pipe(takeUntil(this.destroy$))
      .subscribe((roleContext) => {
        if (roleContext) {
          // Use the exact role context from AuthService (includes specific market_id)
          this.userRole = roleContext.role_name;
          this.userRoleCode = roleContext.role_code?.toUpperCase() || '';
          this.userMarket = roleContext.market_name || 'N/A';
          this.userMarketId = roleContext.market_id ?? null;

          // For BU Admin, set business unit names
          if (roleContext.role_code?.toUpperCase() === 'BUADMIN' && roleContext.business_units?.length) {
            this.userBusinessUnits = roleContext.business_units.map(bu => bu.business_unit_name).join(', ');
          } else {
            this.userBusinessUnits = '';
          }

          const inputUser = this.currentUserInput();
          if (inputUser) {
            this.userName = `${inputUser.first_name} ${inputUser.last_name}`;
          }

          this.setPermissionsFromBackend({ system_role_info: [roleContext] });
          this.loadingEvent.emit(false);
          this.cdr.markForCheck();
        } else {
          // Fallback to input-based logic if no role context yet
          this.updateRoleAndPermissions();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRoleInput'] || changes['currentUserInput']) {
      this.updateRoleAndPermissions();
    }
  }


  private updateRoleAndPermissions(): void {
    const inputRole = this.userRoleInput();
    const inputUser = this.currentUserInput();

    if (inputUser && inputUser.system_role_info && inputUser.system_role_info.length > 1) {
  const selectedRoleObj = inputUser.system_role_info.find(r => r.role_name === inputRole) || inputUser.system_role_info[0];
      this.userRole = selectedRoleObj.role_name;
      this.userRoleCode = selectedRoleObj.role_code?.toUpperCase() || '';
      this.userName = `${inputUser.first_name} ${inputUser.last_name}`;
      this.userMarket = (selectedRoleObj && ('market_name' in selectedRoleObj || 'market' in selectedRoleObj))
        ? ((selectedRoleObj as { market_name?: string; market?: string }).market_name || (selectedRoleObj as { market?: string }).market || inputUser.market || 'N/A')
        : (inputUser.market || 'N/A');
      this.userMarketId = selectedRoleObj.market_id ?? inputUser.market_id ?? null;
      // For BU Admin, set business unit names
      if (selectedRoleObj.role_code?.toUpperCase() === 'BUADMIN' && selectedRoleObj.business_units?.length) {
        this.userBusinessUnits = selectedRoleObj.business_units.map(bu => bu.business_unit_name).join(', ');
      } else {
        this.userBusinessUnits = '';
      }
      this.setPermissionsFromBackend({ system_role_info: [selectedRoleObj] });
  // ...existing code...
      this.loadingEvent.emit(false);
      this.cdr.markForCheck();
    } else if (inputUser && inputUser.system_role_info && inputUser.system_role_info.length === 1) {
  const singleRoleObj = inputUser.system_role_info[0];
      this.userRole = singleRoleObj.role_name;
      this.userRoleCode = singleRoleObj.role_code?.toUpperCase() || '';
      this.userName = `${inputUser.first_name} ${inputUser.last_name}`;
      this.userMarket = (singleRoleObj && ('market_name' in singleRoleObj || 'market' in singleRoleObj))
        ? ((singleRoleObj as { market_name?: string; market?: string }).market_name || (singleRoleObj as { market?: string }).market || inputUser.market || 'N/A')
        : (inputUser.market || 'N/A');
      this.userMarketId = singleRoleObj.market_id ?? inputUser.market_id ?? null;
      // For BU Admin, set business unit names
      if (singleRoleObj.role_code?.toUpperCase() === 'BUADMIN' && singleRoleObj.business_units?.length) {
        this.userBusinessUnits = singleRoleObj.business_units.map(bu => bu.business_unit_name).join(', ');
      } else {
        this.userBusinessUnits = '';
      }
      this.setPermissionsFromBackend({ system_role_info: [singleRoleObj] });
  // ...existing code...
      this.loadingEvent.emit(false);
      this.cdr.markForCheck();
    } else {
      // Fallback: load from backend/auth session
      this.loadCurrentUser();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentUser(): void {
    // No backend call; fallback does nothing, as all data should come from parent
    this.errorMessages = 'No user data available from parent.';
    this.loadingEvent.emit(false);
    this.cdr.markForCheck();
  }

  /**
   * Set permissions from backend system_role_info
   */
  private setPermissionsFromBackend(user: { system_role_info: SystemRole[] }): void {
    // Use first role in system_role_info
    const roleObj = user.system_role_info && user.system_role_info.length > 0 ? user.system_role_info[0] : null;
    if (!roleObj || !roleObj.privileges) {
      this.userPrivileges = new Set();
      return;
    }
    // Extract privilege names from the privileges array
    this.userPrivileges = new Set(roleObj.privileges.map(p => p.privilege_name));
  }

  /**
   * Fallback: Extract user info from authentication session when backend is unavailable
   */
  private extractUserFromAuthSession(_authUser: unknown): void {
    this.userRole = 'Business Unit Admin';
    this.userName = 'User';
    this.userMarket = 'N/A';
    this.userPrivileges = new Set();
  }

  // Helper method to check if user has a specific privilege
  private hasPrivilege(privilegeName: string): boolean {
    return this.userPrivileges.has(privilegeName);
  }

  // Permission getters for template based on privileges
  get canManageUsers(): boolean {
    return this.hasPrivilege('USER_MANAGEMENT');
  }

  get canManageMasterData(): boolean {
    return this.hasPrivilege('MASTER_DATA_MANAGEMENT');
  }

  get canManageCycles(): boolean {
    return this.hasPrivilege('CYCLE_MANAGEMENT');
  }

  get canViewCycles(): boolean {
    return this.hasPrivilege('VIEW_CYCLE');
  }

  get canManageForms(): boolean {
    return this.hasPrivilege('FORM_MANAGEMENT');
  }

  get canViewAdminDashboard(): boolean {
    return this.hasPrivilege('ADMIN_DASHBOARD');
  }

  get canViewManagerDashboard(): boolean {
    return this.hasPrivilege('MANAGER_DASHBOARD');
  }

  get canViewComplianceRecords(): boolean {
    return this.userRoleCode === 'COMPBP';
  }

  // Returns the role name from backend (first role in system_role_info)
  get roleGreeting(): string {
    return this.userRole || '';
  }
}
